/* eslint-disable max-lines */
import { captureError } from '@smg-real-estate/browser-rum';
import { type HgCurrencyInputModel } from '@smg-real-estate/domus';
import type {
  Address,
  Attachment,
  Category,
  Characteristics,
  ContactForm,
  GeoCoordinates,
  Lister,
  Listing,
  Localization,
  Person,
  Url,
  ValueAddedServices,
} from '@smg-real-estate/hg-rets-schema';
import {
  AreaThePriceRefersTo,
  Country,
  OfferType,
  PriceInterval,
  RankType,
  Size,
} from '@smg-real-estate/hg-rets-schema';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  getUnifiedInsertionById,
  patchInsertionStatus,
  postInsertion,
  postInsertionCustomerSegment,
  postInsertionSubmittal,
  putInsertion,
  putInsertionFurthestPage,
} from '@/api/insertions';
import type { UserDetails } from '@/api/user';
import {
  setListingWebsiteLink,
  setListingYoutubeLink,
  setVirtualTourLink,
} from '@/helpers/ListingAttachment/ListingAttachment';
import { clearInapplicableCharacteristics } from '@/helpers/ListingFeatureMappings/featureMappingBusiness';
import { getCurrentDateInHgRetsFormat, mapToDotFormattedDate } from '@/helpers/dateUtils';
import { isCommercialObject } from '@/helpers/isCommercialObject';
import { objectTypeMappings } from '@/helpers/objectTypeMappings';
import { applyProjectBoostToNewConstructionData } from '@/helpers/projectBoost';
import { isDateFormat } from '@/helpers/validations';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useChatRelayStore } from '@/stores/chatRelay/useChatRelayStore';
import { useInsertionsStore } from '@/stores/insertions/useInsertionsStore';
import { useSeonStore } from '@/stores/seon/useSeonStore';
import { ApiCallStatus, getMessageForStatus } from '@/stores/types/insertions';
import { useUserStore } from '@/stores/user/useUserStore';
import {
  AvailableFrom,
  Currency,
  CustomerSegment,
  InsertionStatus,
  PagesFlowOrder,
  type Insertion,
  type QueryData,
} from '@/types';

export function getInitialState(): Insertion {
  return {
    createdAt: new Date(),
    id: '',
    status: InsertionStatus.Draft,
    userId: '',
    objectReference: '',
    username: '',
    lastSavedHash: '',
    lastSavedAt: undefined,
    directPaymentInfo: '',
    customerSegment: CustomerSegment.PROFESSIONAL,
    furthestPage: '',
    shouldHideAddress: undefined,
    listing: {
      id: '',
      externalIds: {
        propertyReferenceId: '',
        refObject: '',
        refHouse: '',
        refProperty: '',
      },
      offerType: OfferType.Rent,
      availableFrom: getCurrentDateInHgRetsFormat(),
      categories: [],
      address: {
        street: '',
        postalCode: '',
        locality: undefined,
        country: Country.Ch,
        region: undefined,
      },
      characteristics: {
        numberOfRooms: undefined,
        numberOfBathrooms: undefined,
        floor: undefined,
        lotSize: undefined,
        ceilingHeight: undefined,
        cubage: undefined,
        yearBuilt: undefined,
        yearLastRenovated: undefined,
        numberOfFloors: undefined,
        totalFloorSpace: undefined,
        livingSpace: undefined,
        isWheelchairAccessible: undefined,
        isChildFriendly: undefined,
        arePetsAllowed: undefined,
        isSmokingAllowed: undefined,
        hasBalcony: undefined,
        hasGarage: undefined,
        hasParking: undefined,
        hasElevator: undefined,
        hasFireplace: undefined,
        hasWashingMachine: undefined,
        hasCableTv: undefined,
        hasNiceView: undefined,
        isQuiet: undefined,
        isMinergieCertified: undefined,
        isNewBuilding: undefined,
        isOldBuilding: undefined,
        floorLoad: undefined,
        craneCapacity: undefined,
        elevatorCapacity: undefined,
      },
      contactForm: {
        size: Size.NoAddress,
      },
      valueAddedServices: {
        bundle: '',
      },
      lister: {
        name: undefined,
        email: undefined,
        phone: undefined,
        billing: undefined,
        contacts: {
          viewing: {
            phone: undefined,
            givenName: undefined,
            note: undefined,
          },
          inquiry: {
            givenName: undefined,
            familyName: undefined,
          },
        },
      },
      localization: {
        de: {
          text: {
            description: '',
            title: '',
          },
          attachments: [],
          urls: [],
        },
        primary: 'de',
      },
      prices: {
        currency: Currency.CHF,
        rent: {
          extra: undefined,
          net: undefined,
          gross: undefined,
          interval: undefined,
          area: undefined,
        },
        buy: {
          price: undefined,
        },
      },
    },
  };
}

const getOfferTypeFromQueryData = (offer?: string, listingOfferType?: OfferType): OfferType => {
  if (!listingOfferType) {
    return offer === 'SALE' ? OfferType.Buy : OfferType.Rent;
  }
  return listingOfferType;
};

const getCategoriesFromQueryData = (type?: string): Category[] => {
  return objectTypeMappings[type?.toUpperCase() as string];
};

export interface NewInsertionFromListingPayload {
  listingOfferType?: OfferType;
  listingCategoriesMapped?: Category[];
  country?: string;
  street?: string;
  zip?: string;
  city?: string;
  canton?: string;
  offer?: string;
  type?: string;
}

export type InitializeInsertionPayload = QueryData | NewInsertionFromListingPayload;

const cleanupLocalizationUrls = (
  localization: Localization | undefined,
): Localization | undefined => {
  if (!localization) {
    return undefined;
  }

  if (!localization.de || !localization.de.urls) {
    return localization;
  }

  return {
    ...localization,
    de: {
      ...localization.de,
      urls: localization.de.urls.map((item: any) => {
        Object.keys(item).forEach((key) => {
          if (!item[key]) {
            delete item[key];
          }
        });
        return item;
      }),
    },
  };
};

const ensureViewingContact = (state: Insertion): Person => {
  if (!state.listing.lister) {
    state.listing.lister = {};
  }
  if (!state.listing.lister.contacts) {
    state.listing.lister.contacts = {};
  }
  if (!state.listing.lister.contacts.viewing) {
    state.listing.lister.contacts.viewing = {};
  }
  return state.listing.lister.contacts.viewing;
};

export const useInsertionStore = defineStore(
  'insertion',
  // eslint-disable-next-line max-statements -- setup store returns state, computeds, mutators, and actions together
  () => {
    const insertion = ref<Insertion>(getInitialState());

    const getInsertion = computed((): Insertion => insertion.value);
    const getInsertionListing = computed((): Listing => insertion.value.listing);
    const getInsertionListingId = computed(
      (): string | null | undefined => insertion.value.listing.id,
    );
    const getInsertionId = computed((): string => insertion.value.id);
    const getInsertionStatus = computed((): InsertionStatus => insertion.value.status);
    const getShouldHideAddress = computed(
      (): boolean | undefined => insertion.value.shouldHideAddress,
    );
    const getPriceWithCurrency = computed((): HgCurrencyInputModel => {
      let price;
      if (OfferType.Rent === insertion.value.listing.offerType) {
        price =
          insertion.value.listing.prices?.rent?.net || insertion.value.listing.prices?.rent?.gross;
      }
      if (OfferType.Buy === insertion.value.listing.offerType) {
        price = insertion.value.listing.prices?.buy?.price;
      }
      return {
        price,
        currency: insertion.value.listing.prices?.currency ?? Currency.CHF,
      } as HgCurrencyInputModel;
    });
    const getAvailableFromDate = computed((): string | undefined => {
      if (insertion.value.listing.availableFrom !== undefined) {
        const formattedDate = mapToDotFormattedDate(insertion.value.listing.availableFrom);
        return isDateFormat(formattedDate) ? formattedDate : insertion.value.listing.availableFrom;
      }
      return undefined;
    });
    const getAvailableFromType = computed((): AvailableFrom => {
      if (insertion.value.listing.availableFrom !== undefined) {
        const formattedDate = mapToDotFormattedDate(insertion.value.listing.availableFrom);
        if (
          isDateFormat(formattedDate) &&
          new Date(insertion.value.listing.availableFrom) <= new Date()
        ) {
          return AvailableFrom.IMMEDIATELY;
        }
        return AvailableFrom.DATE;
      }
      return AvailableFrom.BY_AGREEMENT;
    });
    const getListerViewingPhone = computed(
      (): string | undefined => insertion.value.listing.lister?.contacts?.viewing?.phone,
    );
    const getListerViewingName = computed(
      (): string | undefined => insertion.value.listing?.lister?.contacts?.viewing?.givenName,
    );
    const getInsertionRegion = computed((): string | undefined => {
      return insertion.value.listing?.address?.region;
    });
    const getInsertionObjectReference = computed((): string | undefined => {
      return insertion.value.listing.externalIds?.propertyReferenceId;
    });
    const getListingLocality = computed((): string | undefined => {
      return insertion.value.listing.address?.locality;
    });
    const getListingCategories = computed((): Category[] => {
      return (insertion.value.listing.categories || []) as Category[];
    });
    const getListingTitle = computed((): string | undefined => {
      return insertion.value.listing.localization?.de?.text?.title;
    });
    const getIsLifetimeTopListing = computed((): boolean => {
      return insertion.value.listing.valueAddedServices?.isLifetimeToplisting || false;
    });
    const getListingAttachments = computed((): Attachment[] => {
      return insertion.value.listing.localization?.de?.attachments || [];
    });

    function assignInsertion(newInsertion: Insertion): void {
      insertion.value = { ...newInsertion };
    }

    function setShouldHideAddress(shouldHideAddress: boolean | undefined): void {
      insertion.value.shouldHideAddress = shouldHideAddress;
    }

    function setId(id: string): void {
      insertion.value.id = id;
    }

    function setLastSaved(): void {
      insertion.value.lastSavedAt = new Date();
    }

    function setListing(listing: Listing): void {
      insertion.value.listing = listing;
    }

    function setOfferType(offerType: OfferType): void {
      if (OfferType.Rent === offerType) {
        insertion.value.listing.prices = {
          ...insertion.value.listing.prices,
          ...{
            buy: getInitialState().listing.prices?.buy,
          },
        };
      }
      if (OfferType.Buy === offerType) {
        insertion.value.listing.prices = {
          ...insertion.value.listing.prices,
          ...{
            rent: getInitialState().listing.prices?.rent,
          },
        };
      }
      insertion.value.listing.offerType = offerType;
    }

    function setCategories(categories: Category[]): void {
      clearInapplicableCharacteristics(insertion.value, categories);

      insertion.value.listing.categories = categories;
    }

    function setPhone(phone: string): void {
      (insertion.value.listing.lister as Lister).phone = phone;
    }

    function setListingStreetAndNumber(streetAndNumber?: string): void {
      (insertion.value.listing.address as Address).street = streetAndNumber;
    }

    function setListingZipCode(zipCode?: string): void {
      (insertion.value.listing.address as Address).postalCode = zipCode;
    }

    function setListingCity(city?: string): void {
      (insertion.value.listing.address as Address).locality = city;
    }

    function setListingCountry(country?: Country): void {
      (insertion.value.listing.address as Address).country = country;
    }

    function setListingRegion(region?: string): void {
      (insertion.value.listing.address as Address).region = region;
    }

    function setContactGivenName(name: string): void {
      (insertion.value.listing.lister?.contacts?.inquiry as Person).givenName = name;
    }

    function setContactFamilyName(familyName: string): void {
      (insertion.value.listing.lister?.contacts?.inquiry as Person).familyName = familyName;
    }

    function setEmail(email: string): void {
      (insertion.value.listing?.lister as Lister).email = email;
    }

    function setTitle(value: string): void {
      (insertion.value.listing.localization?.de?.text as any).title = value;
    }

    function setDescription(value: string): void {
      (insertion.value.listing.localization?.de?.text as any).description = value;
    }

    function setBundle(bundleMatchingId: string): void {
      (insertion.value.listing.valueAddedServices as ValueAddedServices).bundle = bundleMatchingId;
    }

    function setIsLifetimeTopListing(isLifetimeTopListing?: boolean): void {
      (insertion.value.listing.valueAddedServices as ValueAddedServices).isLifetimeToplisting =
        isLifetimeTopListing;
    }

    function setRankType(payload?: { rankType: RankType; start: Date; end?: Date }): void {
      if (!payload) {
        (insertion.value.listing.valueAddedServices as ValueAddedServices).rank = undefined;
        return;
      }

      (insertion.value.listing.valueAddedServices as ValueAddedServices).rank = {
        start: payload.start.toISOString(),
        end: payload.end?.toISOString(),
        type: payload.rankType,
      };
    }

    function setProjectBoostSelection(enabled: boolean): void {
      insertion.value.listing.newConstructionData = applyProjectBoostToNewConstructionData(
        insertion.value.listing.newConstructionData,
        enabled,
      );
    }

    function setPropertyOwnerForBilling(refOwner: string): void {
      const PUBLISHER_ID = 'smg';
      const KEY = 'cost-forwarding-ref-owner';

      const publishers = insertion.value.listing.publishers || []; // Default to empty array if undefined
      const publisherToUpdate = publishers.find((publisher) => publisher.id === PUBLISHER_ID);

      // Create new publisher if 'smg' doesn't exist
      if (!publisherToUpdate) {
        publishers.push({
          id: PUBLISHER_ID,
          options: [
            {
              key: KEY,
              value: refOwner,
            },
          ],
        });
      }
      // If the publisher with id 'smg' exists, check if it has an option with key 'cost-forwarding-ref-owner'
      else {
        publisherToUpdate.options = publisherToUpdate.options || []; // Default to empty array if undefined

        const optionToUpdate = publisherToUpdate.options.find((option) => option.key === KEY);

        if (!optionToUpdate) {
          // Create new option if 'cost-forwarding-ref-owner' doesn't exist
          publisherToUpdate.options?.push({ key: KEY, value: refOwner });
        } else {
          // Update existing option with new value
          optionToUpdate.value = refOwner;
        }
      }

      insertion.value.listing.publishers = publishers;
    }

    function setNumberOfRooms(numberOfRooms: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).numberOfRooms = numberOfRooms;
    }

    function setNumberOfBathrooms(numberOfBathrooms: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).numberOfBathrooms =
        numberOfBathrooms;
    }

    function setLivingSpace(livingSpace: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).livingSpace = livingSpace;
    }

    function setFloor(floor: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).floor = floor;
    }

    function setLotSize(lotSize: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).lotSize = lotSize;
    }

    function setCeilingHeight(ceilingHeight: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).ceilingHeight = ceilingHeight;
    }

    function setYearBuilt(yearBuilt: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).yearBuilt = yearBuilt;
    }

    function setYearLastRenovated(yearLastRenovated: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).yearLastRenovated =
        yearLastRenovated;
    }

    function setNumberOfFloors(numberOfFloors: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).numberOfFloors = numberOfFloors;
    }

    function setNumberOfApartments(numberOfApartments: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).numberOfApartments =
        numberOfApartments;
    }

    function setCubage(cubage: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).cubage = cubage;
    }

    function setTotalFloorSpace(totalFloorSpace: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).totalFloorSpace =
        totalFloorSpace;
    }

    function setSingleFloorSpace(singleFloorSpace: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).singleFloorSpace =
        singleFloorSpace;
    }

    function setRentPriceExtra(extra: number | undefined): void {
      insertion.value.listing.prices = {
        ...insertion.value.listing.prices,
        ...{
          rent: {
            ...insertion.value.listing.prices?.rent,
            extra,
          },
        },
      };
    }

    function setRentPriceNet(net: number | undefined): void {
      insertion.value.listing.prices = {
        ...insertion.value.listing.prices,
        ...{
          rent: {
            ...insertion.value.listing.prices?.rent,
            net,
          },
        },
      };
    }

    function setRentPriceGross(gross: number | undefined): void {
      insertion.value.listing.prices = {
        ...insertion.value.listing.prices,
        ...{
          rent: {
            ...insertion.value.listing.prices?.rent,
            gross,
          },
        },
      };
    }

    function setPriceInterval(priceInterval: PriceInterval): void {
      insertion.value.listing.prices = {
        ...insertion.value.listing.prices,
        ...{
          rent: {
            ...insertion.value.listing.prices?.rent,
            interval: priceInterval,
          },
        },
      };
    }

    function setAreaThePriceRefersTo(area: AreaThePriceRefersTo): void {
      insertion.value.listing.prices = {
        ...insertion.value.listing.prices,
        ...{
          rent: {
            ...insertion.value.listing.prices?.rent,
            area,
          },
        },
      };
    }

    function setPriceCurrency(currency: string): void {
      insertion.value.listing.prices = {
        ...insertion.value.listing.prices,
        ...{
          currency,
        },
      };
    }

    function setBuyPrice(price: number | undefined): void {
      insertion.value.listing.prices = {
        ...insertion.value.listing.prices,
        ...{
          buy: {
            ...insertion.value.listing.prices?.buy,
            price,
          },
        },
      };
    }

    function setGeoCoordinates(geoCoordinates: GeoCoordinates): void {
      (insertion.value.listing.address as Address).geoCoordinates = {
        latitude: geoCoordinates.latitude,
        longitude: geoCoordinates.longitude,
      };
    }

    function removeGeoCoordinates(): void {
      (insertion.value.listing.address as Address).geoCoordinates = undefined;
    }

    function setStatus(status: InsertionStatus): void {
      insertion.value.status = status;
    }

    function setObjectReferenceId(status: string): void {
      if (insertion.value.listing.externalIds) {
        insertion.value.listing.externalIds.propertyReferenceId = status;
        insertion.value.listing.externalIds.internalReferenceId = status;
      }
    }

    function setAttachments(value: Attachment[]): void {
      if (insertion.value.listing.localization?.de) {
        insertion.value.listing.localization.de.attachments = value;
      }
    }

    function setAvailableFrom(value: string | undefined): void {
      insertion.value.listing.availableFrom = value;
    }

    function updateInsertionStatus(status: InsertionStatus): void {
      insertion.value.status = status;
    }

    function toggleListingFeature(feature: string): void {
      const oldFeatureValue = (insertion.value.listing.characteristics as any)[feature];
      if (oldFeatureValue) {
        (insertion.value.listing.characteristics as Characteristics)[
          feature as keyof Characteristics
        ] = undefined;
      } else {
        (insertion.value.listing.characteristics as Characteristics)[
          feature as keyof Characteristics
        ] = true as any;
      }
    }

    function setListerViewingName(name: string): void {
      ensureViewingContact(insertion.value).givenName = name;
    }

    function setListerViewingPhone(phone: string): void {
      ensureViewingContact(insertion.value).phone = phone;
    }

    function setViewingNote(viewingNote: string): void {
      ensureViewingContact(insertion.value).note = viewingNote;
    }

    function resetViewingContact(): void {
      const contacts = insertion.value.listing.lister?.contacts;
      if (!contacts?.viewing) {
        return;
      }
      // preserve note, but clear other fields
      const { note } = contacts.viewing;
      contacts.viewing = { note };
    }

    function setContactFormType(contactFormType: string): void {
      (insertion.value.listing.contactForm as ContactForm).size = contactFormType as Size;
    }

    function setFurthestPage(furthestPage: PagesFlowOrder): void {
      /*
    FurthestPage is stored on the Insertion as a string, so to compare it must be converted back to the PagesFlowOrder enum.
    If the new value is a higher enum than the old value, the enum key of the new value is written out as a string.
     */
      const currentFurthestPage = insertion.value.furthestPage as string;
      type PagesFlowOrderStrings = keyof typeof PagesFlowOrder;
      if (
        !currentFurthestPage ||
        furthestPage > PagesFlowOrder[currentFurthestPage as PagesFlowOrderStrings]
      ) {
        insertion.value.furthestPage = PagesFlowOrder[furthestPage];
      }
    }

    function setWebsiteUrl(urlData: Url): void {
      if (insertion.value.listing.localization?.de) {
        const urls = setListingWebsiteLink(
          insertion.value.listing.localization?.de?.urls as Url[],
          urlData,
        );
        insertion.value.listing.localization.de.urls = urls;
      }
    }

    function setYoutubeUrl(url: string): void {
      if (insertion.value.listing.localization?.de) {
        const urls = setListingYoutubeLink(
          insertion.value.listing.localization?.de?.urls as Url[],
          url,
        );
        insertion.value.listing.localization.de.urls = urls;
      }
    }

    function setVirtualTour(url: string): void {
      if (insertion.value.listing.localization?.de) {
        const urls = setVirtualTourLink(
          insertion.value.listing.localization?.de?.urls as Url[],
          url,
        );
        insertion.value.listing.localization.de.urls = urls;
      }
    }

    function removeListerAddressAndNames(): void {
      (insertion.value.listing.lister as Lister).address = undefined;
      (insertion.value.listing.lister as Lister).name = undefined;
      (insertion.value.listing.lister as Lister).legalName = undefined;
    }

    function setListerName(name: string): void {
      (insertion.value.listing.lister as Lister).name = name;
    }

    function setListerLegalName(legalName: string): void {
      (insertion.value.listing.lister as Lister).legalName = legalName;
    }

    function setListerStreetAndNumber(streetAndNumber: string): void {
      (insertion.value.listing.lister as Lister).address = {
        ...insertion.value.listing.lister?.address,
        street: streetAndNumber,
      };
    }

    function setListerCountry(country: Country): void {
      (insertion.value.listing.lister as Lister).address = {
        ...insertion.value.listing.lister?.address,
        country,
      };
    }

    function setListerZipCode(postalCode: string): void {
      (insertion.value.listing.lister as Lister).address = {
        ...insertion.value.listing.lister?.address,
        postalCode: postalCode.toString(),
      };
    }

    function setListerCity(city: string): void {
      (insertion.value.listing.lister as Lister).address = {
        ...insertion.value.listing.lister?.address,
        locality: city,
      };
    }

    function setFloorLoad(floorLoad: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).floorLoad = floorLoad;
    }

    function setCraneCapacity(craneCapacity: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).craneCapacity = craneCapacity;
    }

    function setElevatorCapacity(elevatorCapacity: number | undefined): void {
      (insertion.value.listing.characteristics as Characteristics).elevatorCapacity =
        elevatorCapacity;
    }

    async function setInsertion(nextInsertion: Insertion): Promise<void> {
      assignInsertion(nextInsertion);
      await useSeonStore().configure({
        insertionId: nextInsertion.id,
        userId: nextInsertion.userId,
      });
    }

    function clearInsertionState(): void {
      insertion.value = getInitialState();
      useSeonStore().clear();
    }

    function initializeNewInsertion({
      offer,
      type,
      country,
      city,
      zip,
      street,
      canton,
      listingOfferType,
      listingCategoriesMapped,
    }: InitializeInsertionPayload): void {
      const offerType: OfferType = getOfferTypeFromQueryData(offer, listingOfferType);
      const categories: Category[] = listingCategoriesMapped || getCategoriesFromQueryData(type);

      setOfferType(offerType);
      setCategories(categories);
      setListingStreetAndNumber(street);
      setListingZipCode(zip);
      setListingCity(city);
      setListingCountry(country as Country);
      setListingRegion(canton);
      setFurthestPage(PagesFlowOrder.DETAILS);

      if (offerType === OfferType.Rent) {
        setPriceInterval(PriceInterval.Month);
        if (isCommercialObject(categories)) {
          setAreaThePriceRefersTo(AreaThePriceRefersTo.All);
        }
      }
    }

    async function fetchInsertionById(payload: {
      userId: string;
      insertionId: string;
    }): Promise<void> {
      const sourcePlatform = useUserStore().sourcePlatform;
      const fetchedInsertion = await getUnifiedInsertionById(
        payload.userId,
        payload.insertionId,
        sourcePlatform,
      );

      // Temporary fix to remove unwanted null values from urls.
      // See also https://homegate.atlassian.net/browse/HOM-1569
      fetchedInsertion.listing.localization = cleanupLocalizationUrls(
        fetchedInsertion.listing.localization,
      );
      fetchedInsertion.listing.characteristics = fetchedInsertion.listing.characteristics || {};

      await setInsertion(fetchedInsertion);
      useChatRelayStore().clearImageDescriptions();
    }

    function updateListingStreetAndNumber(streetAndNumber: string): void {
      setListingStreetAndNumber(streetAndNumber);
    }

    function updateListingZipAndCity(zipAndCity: string): void {
      const split = zipAndCity.split(',', 2);
      setListingZipCode(split[0].trim());
      setListingCity(split[1].trim());
    }

    function updateRentPricesWithAdditionalCosts({
      extra,
      extraIncluded,
    }: {
      extra?: number;
      extraIncluded?: boolean;
    }): void {
      const price = getPriceWithCurrency.value.price;
      const priceExtra = extra ? Math.round(extra) : undefined;
      setRentPriceExtra(priceExtra);
      updatePrice({ price, priceExtra, extraIncluded });
    }

    function updatePriceWithCurrency(
      data: HgCurrencyInputModel & { extraIncluded?: boolean },
    ): void {
      setPriceCurrency(data.currency);

      if (OfferType.Rent === insertion.value.listing.offerType) {
        const price = data.price ? Math.round(data.price) : undefined;
        const priceExtra = insertion.value.listing.prices?.rent?.extra;
        updatePrice({
          price,
          priceExtra,
          extraIncluded: data.extraIncluded,
        });
      }

      if (OfferType.Buy === insertion.value.listing.offerType) {
        setBuyPrice(data.price);
      }
    }

    function updatePrice({
      price,
      priceExtra,
      extraIncluded,
    }: {
      price: number | undefined;
      priceExtra?: number;
      extraIncluded?: boolean;
    }): void {
      if (price && !extraIncluded) {
        const grossPrice = priceExtra ? price + priceExtra : undefined;
        setRentPriceNet(price);
        setRentPriceGross(grossPrice);
      } else {
        setRentPriceNet(undefined);
        setRentPriceGross(price);
      }
    }

    async function createNewInsertion(queryData: InitializeInsertionPayload): Promise<string> {
      await initializeNewInsertion(queryData);
      const userId = useAgencyStore().activeAgencyPersonId as string;
      const sourcePlatform = useUserStore().sourcePlatform;
      const createdInsertion = await postInsertion(userId, insertion.value.listing, sourcePlatform);

      putInsertionFurthestPage(
        userId,
        createdInsertion.id,
        PagesFlowOrder[PagesFlowOrder.DETAILS],
        sourcePlatform,
      ).catch(captureError);

      postInsertionCustomerSegment(userId, createdInsertion.id, sourcePlatform).catch(captureError);

      //preserve flag
      const shouldHideAddress = getShouldHideAddress.value;
      await setInsertion(createdInsertion);
      setShouldHideAddress(shouldHideAddress);

      return createdInsertion.id;
    }

    async function saveInsertion(): Promise<void> {
      const ownerId = insertion.value.userId;
      const insertionId = getInsertionId.value;
      const sourcePlatform = useUserStore().sourcePlatform;

      await putInsertion(ownerId, insertionId, insertion.value.listing, sourcePlatform);
      setLastSaved();
    }

    async function saveAndSubmitForApproval(): Promise<void> {
      const ownerId = insertion.value.userId;
      const insertionId = getInsertionId.value;
      const sourcePlatform = useUserStore().sourcePlatform;
      const {
        enrolledInMFA: userEnrolledInMFA,
        requiredMFA,
        mfaEnrollmentDateUtc,
      } = useUserStore().userDetails as UserDetails;

      await putInsertion(ownerId, insertionId, insertion.value.listing, sourcePlatform);
      setLastSaved();

      await useSeonStore().saveSeonSession({
        ownerId,
        insertionId,
        userEnrolledInMFA,
        requiredMFA,
        mfaEnrollmentDateUtc,
      });
      const submittedInsertion = await postInsertionSubmittal(ownerId, insertionId, sourcePlatform);
      assignInsertion({ ...insertion.value, ...submittedInsertion });
      if (submittedInsertion) {
        await useInsertionsStore().registerInsertionAfterPublish(submittedInsertion);
      }
    }

    async function changeStatus({
      insertion: targetInsertion,
      status,
    }: {
      insertion: Insertion;
      status: InsertionStatus;
    }): Promise<void> {
      const insertionsStore = useInsertionsStore();
      try {
        insertionsStore.apiCallStatus = ApiCallStatus.IN_PROGRESS;

        const sourcePlatform = useUserStore().sourcePlatform;
        await patchInsertionStatus(
          targetInsertion.userId,
          targetInsertion.id,
          status,
          sourcePlatform,
        );
        insertionsStore.apiCallStatus = ApiCallStatus.SUCCESS;

        updateInsertionStatus(status);
        insertionsStore.setListingActionSuccess(getMessageForStatus(status));
      } catch (error) {
        insertionsStore.apiCallStatus = ApiCallStatus.ERROR;
        captureError(error);
      }
    }

    async function updateFurthestPage(furthestPage: PagesFlowOrder): Promise<void> {
      const ownerId = insertion.value.userId;
      const insertionId = getInsertionId.value;
      const sourcePlatform = useUserStore().sourcePlatform;

      /*
    FurthestPage is stored on the Insertion as a string, so to compare it must be converted back to the PagesFlowOrder enum.
    If the new value is a higher enum than the old value, the enum key of the new value is written out as a string.
     */
      const currentFurthestPage = insertion.value.furthestPage as string;
      type PagesFlowOrderStrings = keyof typeof PagesFlowOrder;
      if (
        !currentFurthestPage ||
        furthestPage > PagesFlowOrder[currentFurthestPage as PagesFlowOrderStrings]
      ) {
        setFurthestPage(furthestPage);
        if (!insertion.value.furthestPage) {
          return;
        }
        try {
          await putInsertionFurthestPage(
            ownerId,
            insertionId,
            insertion.value.furthestPage,
            sourcePlatform,
          );
        } catch (error) {
          captureError(error);
        }
      }
    }

    return {
      insertion,
      getInsertion,
      getInsertionListing,
      getInsertionListingId,
      getInsertionId,
      getInsertionStatus,
      getShouldHideAddress,
      getPriceWithCurrency,
      getAvailableFromDate,
      getAvailableFromType,
      getListerViewingPhone,
      getListerViewingName,
      getInsertionRegion,
      getInsertionObjectReference,
      getListingLocality,
      getListingCategories,
      getListingTitle,
      getIsLifetimeTopListing,
      getListingAttachments,
      assignInsertion,
      setShouldHideAddress,
      setId,
      setLastSaved,
      setListing,
      setOfferType,
      setCategories,
      setPhone,
      setListingStreetAndNumber,
      setListingZipCode,
      setListingCity,
      setListingCountry,
      setListingRegion,
      setContactGivenName,
      setContactFamilyName,
      setEmail,
      setTitle,
      setDescription,
      setBundle,
      setIsLifetimeTopListing,
      setRankType,
      setProjectBoostSelection,
      setPropertyOwnerForBilling,
      setNumberOfRooms,
      setNumberOfBathrooms,
      setLivingSpace,
      setFloor,
      setLotSize,
      setCeilingHeight,
      setYearBuilt,
      setYearLastRenovated,
      setNumberOfFloors,
      setNumberOfApartments,
      setCubage,
      setTotalFloorSpace,
      setSingleFloorSpace,
      setRentPriceExtra,
      setRentPriceNet,
      setRentPriceGross,
      setPriceInterval,
      setAreaThePriceRefersTo,
      setPriceCurrency,
      setBuyPrice,
      setGeoCoordinates,
      removeGeoCoordinates,
      setStatus,
      setObjectReferenceId,
      setAttachments,
      setAvailableFrom,
      updateInsertionStatus,
      toggleListingFeature,
      setListerViewingName,
      setListerViewingPhone,
      setViewingNote,
      resetViewingContact,
      setContactFormType,
      setFurthestPage,
      setWebsiteUrl,
      setYoutubeUrl,
      setVirtualTour,
      removeListerAddressAndNames,
      setListerName,
      setListerLegalName,
      setListerStreetAndNumber,
      setListerCountry,
      setListerZipCode,
      setListerCity,
      setFloorLoad,
      setCraneCapacity,
      setElevatorCapacity,
      setInsertion,
      clearInsertionState,
      initializeNewInsertion,
      fetchInsertionById,
      updateListingStreetAndNumber,
      updateListingZipAndCity,
      updateRentPricesWithAdditionalCosts,
      updatePriceWithCurrency,
      updatePrice,
      createNewInsertion,
      saveInsertion,
      saveAndSubmitForApproval,
      changeStatus,
      updateFurthestPage,
    };
  },
);
